import { useMemo, useCallback, useRef, useEffect, memo, useState } from 'react'
import { Table, Checkbox, Spin } from 'antd'
import { LockFilled } from '@ant-design/icons'
import DiscountSuperGroupTable from './DiscountSuperGroupTable'
import EmptyPlaceholder from '../Svg/EmptyPlaceholder'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { handleMultipleTableVirtualScroll, renderTranslation } from '../../Helpers/GenericHelpers'
import { setSelectedBrands } from '../../Slices/Omnichannel/DiscountSlice'
import { addSubRule, toggleSeasonConfirmChangeModal } from '../../Slices/Omnichannel/SeasonRuleSlice'
import {
  isRuleEditToDisable,
  obtainDiscountDataParts,
  retrieveRule,
  retrieveVirtualSubRulesFromSeasonRuleList,
  createNewVirtualSubrule,
  ruleHasSubrules,
  retrieveSubruleId,
  generateSubruleParams,
  isRuleLockedByOtherUsers,
} from '../../Helpers/DiscountHelper'
import DiscountTableHeader from './DiscountTableHeader'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { useNavigateWrapper, generateQueryParams } from '../../Helpers/NavigateHelper'
import { subruleDropdownAll } from '../../Constants/Configuration'
import { calcRuleTableHeight, supergroupsTablesInitialized } from 'Helpers/TableHelper'
import { customEqual as lockEquality } from 'Helpers/LockHelper'
import { TypeOfLock } from 'Services/Omnichannel/LockService'

function DiscountTable(props) {
  const dispatch = useAppDispatch()
  const { navigateToSubrule } = useNavigateWrapper()
  const language = useAppSelector(state => state.present.language.value)
  const discountData = useAppSelector(state => state.present.discount.discountData)
  const seasonRuleList = useAppSelector(state => state.present.seasonRule.seasonRuleList)
  const ruleId = useAppSelector(state => state.present.seasonRule.ruleId)
  const loading = useAppSelector(state => state.present.discount.loading)
  const brandsSelected = useAppSelector(state => state.present.discount.brandsSelected)
  const brandSearch = useAppSelector(state => state.present.discount.brandSearch)
  const hasRuleSelectedChangesToSave = useAppSelector(state => state.present.seasonRule.hasRuleSelectedChangesToSave)
  const isSuperCategoryEnabled = useAppSelector(state => state.present.configuration.isSuperCategoryEnabled)

  const multipleTableContainerRef = useRef<HTMLDivElement>(null)

  const locks = useAppSelector(state => state.present.locks.locks, lockEquality)
  const user = useAppSelector(state => state.present.configuration.userValue)

  const [scrollX, setScrollX] = useState(0)
  const [tableHeight, setTableHeight] = useState(0)

  const [superGroupsMap, brandsMap, sectorDiscountsMap, pricelistDiscountsMap, brandDiscountsMap] = useMemo(() => {
    return obtainDiscountDataParts(discountData)
  }, [discountData])

  const currentRule = useMemo(() => {
    return retrieveRule(ruleId, seasonRuleList)
  }, [ruleId, seasonRuleList])

  const editDisabled = useMemo(() => {
    return (
      isRuleEditToDisable(currentRule, currentRule?.isActive) ||
      isRuleLockedByOtherUsers(locks, currentRule?.id, TypeOfLock.Omnichannel, user)
    )
  }, [currentRule, locks, user])

  const filterBrandBySearchValue = useCallback(
    brandDiscount => {
      if (!brandSearch || brandSearch.length === 0) return brandDiscount

      const lowerSearchValue = brandSearch.toLowerCase()
      return brandDiscount.filter(x => x.name && x.name.toLowerCase().includes(lowerSearchValue))
    },
    [brandSearch],
  )

  const filteredBrands = useMemo(() => {
    if (!brandsMap && brandsMap.size === 0) {
      return []
    }

    const brandNotSelectedFiltered = filterBrandBySearchValue(
      Array.from(brandsMap.values()).filter((x: { name: string }) => brandsSelected.indexOf(x.name) === -1),
    )
    const selectedBrand = Array.from(brandsMap.values()).filter(
      (x: { name: string }) => brandsSelected.indexOf(x.name) !== -1,
    )

    return [...selectedBrand, ...brandNotSelectedFiltered]
  }, [brandsMap, filterBrandBySearchValue, brandsSelected])

  const virtualSubRules = useMemo(() => {
    return retrieveVirtualSubRulesFromSeasonRuleList(seasonRuleList)
  }, [seasonRuleList])

  const createSubrule = useCallback(
    (supergroup, sector, brand) => {
      if (editDisabled) {
        return
      }

      if (hasRuleSelectedChangesToSave) {
        dispatch(toggleSeasonConfirmChangeModal(true))
        return
      }

      // supergroup could be a category or a superCategory
      const queryParams = generateQueryParams(
        generateSubruleParams(isSuperCategoryEnabled, supergroup, sector, brand, subruleDropdownAll),
      )

      if (ruleHasSubrules(currentRule)) {
        const newSubruleData = createNewVirtualSubrule(
          language,
          currentRule,
          virtualSubRules,
          supergroup,
          sector,
          brand,
        )
        dispatch(addSubRule(newSubruleData))

        navigateToSubrule(newSubruleData.virtualSubrule.virtualId, queryParams)
      } else {
        navigateToSubrule(retrieveSubruleId(currentRule), queryParams)
      }
    },
    [editDisabled, hasRuleSelectedChangesToSave, language, currentRule, virtualSubRules, dispatch],
  )

  const discountMainTableColumns = useMemo(() => {
    return [
      {
        title: (
          <DiscountTableHeader
            currentRule={currentRule}
            editDisabled={editDisabled}
            createSubrule={createSubrule}
            currentListing={props.currentListing}
          />
        ),
        dataIndex: 'name',
        key: 'name',
        render: (text, record) => {
          const isBrandSelected = brandsSelected.indexOf(text) !== -1
          return (
            <div className='brand-name-box'>
              {isBrandSelected ? (
                <LockFilled
                  className='general-icon lock-brand-icon'
                  onClick={() => {
                    dispatch(setSelectedBrands(text))
                  }}
                />
              ) : (
                <Checkbox
                  onChange={e => {
                    dispatch(setSelectedBrands(text))
                  }}
                />
              )}
              <div
                className='pl-2 d-inline-block'
                onClick={e => {
                  e.stopPropagation()
                  createSubrule(undefined, undefined, text)
                }}
              >
                {text}
              </div>
            </div>
          )
        },
      },
    ]
  }, [currentRule, editDisabled, createSubrule, props.currentListing, brandsSelected, dispatch])

  const discountMainTableData = useMemo(() => {
    return filteredBrands && filteredBrands.length > 0
      ? filteredBrands.map(x => {
          return {
            key: x.id,
            name: x.name,
          }
        })
      : []
  }, [filteredBrands])

  const setMultipleTableScroll = useCallback(() => {
    if (supergroupsTablesInitialized(superGroupsMap.size) && multipleTableContainerRef?.current) {
      const tableBody = multipleTableContainerRef.current.querySelector('.ant-table-body')
      tableBody && tableBody.addEventListener('scroll', handleMultipleTableVirtualScroll, { passive: true })

      return () => {
        tableBody && tableBody.removeEventListener('scroll', handleMultipleTableVirtualScroll)
      }
    } else {
      setTimeout(setMultipleTableScroll, 1000)
    }
  }, [superGroupsMap])

  useEffect(() => {
    if (superGroupsMap.size > 0) {
      setMultipleTableScroll()
    }
  }, [superGroupsMap])

  let timer = null
  const setScroll = e => {
    const target = e.target as HTMLDivElement
    if (timer !== null) {
      clearTimeout(timer)
    }
    timer = setTimeout(function () {
      setScrollX(target.scrollLeft)
    }, 150)
  }

  useEffect(() => {
    if (currentRule) {
      const tablesContainer = multipleTableContainerRef && multipleTableContainerRef.current
      tablesContainer.addEventListener('scroll', setScroll)

      return () => {
        tablesContainer.removeEventListener('scroll', setScroll)
      }
    }
  }, [currentRule])

  const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
  const keyArrowHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (arrowKeys.includes(e.key)) {
      const focused = document.activeElement

      if (focused && focused.parentElement?.parentElement?.className.indexOf('arrow-navigation') >= 0) {
        const currentTd = focused.closest('td')
        const closestTable = focused.closest('table')
        const cellCount = closestTable.rows[0].cells.length
        const rowCount = closestTable.rows.length
        const cellIncrement = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
        const rowIncrement = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0

        let cellIndex = currentTd.cellIndex + cellIncrement
        let rowIndex = currentTd.closest('tr').rowIndex + rowIncrement

        while (cellIndex >= 0 && rowIndex >= 0 && rowIndex < rowCount && cellIndex < cellCount) {
          const nextTd = closestTable.rows[rowIndex].cells[cellIndex]
          if (nextTd) {
            const inputs = nextTd.getElementsByClassName('ant-input-number-input')
            const nextFocus: any = inputs && inputs.length > 0 ? inputs[0] : null
            if (nextFocus && nextFocus.focus) {
              nextFocus.focus()
              break
            }
          }
          cellIndex += cellIncrement
          rowIndex += rowIncrement
        }
      }
    }
  }

  useEffect(() => {
    const handleResize = () => {
      if (multipleTableContainerRef.current) {
        const newTableHeight = calcRuleTableHeight(multipleTableContainerRef.current.offsetHeight)
        setTableHeight(newTableHeight)
      }
    }
    window.addEventListener('resize', handleResize)

    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [currentRule])

  return currentRule ? (
    <div onKeyUp={keyArrowHandler}>
      <div
        id='discount-tables-container'
        className={
          'multiple-table-container first-fix-left overflow-hidden overflow-x-auto custom-scroll' +
          (editDisabled ? ' noedit' : ' ') +
          (superGroupsMap && superGroupsMap.size > 0 ? '' : ' empty') +
          (loading ? ' load' : '')
        }
        ref={multipleTableContainerRef}
      >
        {loading ? (
          <Spin size='large' style={{ maxHeight: '100%', backgroundColor: 'rgba(0,0,0,0)' }}>
            <div style={{ width: '100vh' }}></div>
          </Spin>
        ) : superGroupsMap && superGroupsMap.size > 0 ? (
          <>
            <Table
              id='discount-main-table'
              className='same-table-scroll'
              scroll={{ y: tableHeight }}
              columns={discountMainTableColumns}
              dataSource={discountMainTableData}
              pagination={false}
            />
            {Array.from(superGroupsMap.values()).map((supergroup: any, index, currentArray) => {
              return (
                <DiscountSuperGroupTable
                  key={supergroup.id ? 'supergroup-table-' + supergroup.id : index}
                  className={index === 0 ? ' first-child' : index === currentArray.length - 1 ? ' last-child' : ''}
                  superGroup={supergroup}
                  filteredBrands={filteredBrands}
                  saveRule={props.saveRule}
                  sectorDiscounts={sectorDiscountsMap}
                  priceListDiscounts={pricelistDiscountsMap}
                  brandDiscounts={brandDiscountsMap}
                  createSubrule={createSubrule}
                  editDisabled={editDisabled}
                  currentRule={currentRule}
                  currentListing={props.currentListing}
                  scrollX={scrollX}
                />
              )
            })}
          </>
        ) : (
          <div className='empty-placeholder'>
            <div className='empty-text'>{renderTranslation(language, TranslationKeys.warningRuleNoArticles)}</div>
            <EmptyPlaceholder className='empty-placeholder-dress' />
          </div>
        )}
      </div>
    </div>
  ) : null
}

export default memo(DiscountTable)
